import { AlertTriangle, CheckCircle2, Mail, User } from "lucide-react";
import { StarDisplay } from "./StarRating";
import { Checkbox } from "./ui/checkbox";
import { ContactCustomerDialog } from "./ContactCustomerDialog";
import { fmtDate } from "../lib/api";

const Highlight = ({ text, term }) => {
  if (!term) return text;
  const parts = text.split(new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
  return parts.map((p, i) => (p.toLowerCase() === term.toLowerCase() ? <mark key={i} className="bg-[#D9A441]/40 text-inherit rounded px-0.5">{p}</mark> : p));
};

const Chip = ({ label, value }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-[#4B5A54]">
    {label} <strong className="text-[#1A2B25]">{value ?? "–"}</strong>
  </span>
);

export const FeedbackCard = ({ item, showBusiness, onToggleAddressed, onContacted, term }) => {
  const low = item.flagged && !item.addressed;
  return (
    <article
      data-testid={item.flagged ? "dashboard-feedback-card-low-rating" : "dashboard-feedback-card"}
      data-feedback-id={item.id}
      data-addressed={item.addressed ? "true" : "false"}
      className={`rounded-2xl border p-5 sm:p-6 transition-[transform,box-shadow,opacity] duration-200 hover:-translate-y-0.5 hover:shadow-md ${
        low ? "border-red-200 border-l-4 border-l-red-500 bg-red-50/60" : item.addressed ? "border-slate-200/80 bg-white/70" : "border-slate-200/80 bg-white"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className={`font-display text-2xl font-extrabold ${item.flagged ? "text-red-600" : "text-[#1A2B25]"}`} data-testid="feedback-overall-score">
            {item.overall}.0
          </span>
          <StarDisplay value={item.overall} />
          {low && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-600 text-white px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide animate-pulse" data-testid="needs-attention-badge">
              <AlertTriangle className="w-3 h-3" /> Needs attention
            </span>
          )}
          {item.matched_keywords?.length > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide" data-testid="feedback-keyword-badge">
              Keyword: {item.matched_keywords.join(", ")}
            </span>
          )}
          {item.addressed && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#E3EDE6] text-[#2F5D4A] px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide" data-testid="feedback-addressed-badge">
              <CheckCircle2 className="w-3 h-3" /> Addressed
            </span>
          )}
        </div>
        <div className="text-right">
          {showBusiness && <p className="text-sm font-semibold text-[#2F5D4A]" data-testid="feedback-business-name">{item.business_name}</p>}
          <p className="text-xs text-[#778580]">{fmtDate(item.created_at)}</p>
        </div>
      </div>
      {item.comment ? (
        <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#1A2B25]" data-testid="feedback-comment">"<Highlight text={item.comment} term={term} />"</p>
      ) : (
        <p className="mt-3 text-sm italic text-[#778580]">No comment left.</p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {(item.rating_labels || []).map((r) => <Chip key={r.label} label={r.label} value={r.value} />)}
        {(item.customer_name || item.customer_email) && (
          <span className="ml-auto flex flex-wrap items-center gap-3 text-xs text-[#4B5A54]" data-testid="feedback-contact">
            {item.customer_name && <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" />{item.customer_name}</span>}
            {item.customer_email && (
              <a href={`mailto:${item.customer_email}`} className="inline-flex items-center gap-1 text-[#2F5D4A] hover:underline">
                <Mail className="w-3.5 h-3.5" />{item.customer_email}
              </a>
            )}
          </span>
        )}
      </div>
      {onToggleAddressed && (
        <div className="mt-4 pt-4 border-t border-slate-200/70 flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2.5 text-sm font-medium text-[#4B5A54] cursor-pointer">
            <Checkbox checked={!!item.addressed} onCheckedChange={(v) => onToggleAddressed(item.id, !!v)} data-testid={`feedback-addressed-checkbox-${item.id}`} />
            Read / addressed
          </label>
          <div className="flex flex-wrap items-center gap-3">
            {item.contacts?.length > 0 && (
              <span className="text-xs text-[#2E6F8E] font-semibold" data-testid={`feedback-contacted-${item.id}`}>
                Emailed {item.contacts.length}× · last {fmtDate(item.contacts[item.contacts.length - 1].sent_at)}
              </span>
            )}
            {item.customer_email && onContacted && <ContactCustomerDialog item={item} onContacted={onContacted} />}
          </div>
        </div>
      )}
    </article>
  );
};
